import React, { useState, useRef, forwardRef } from 'react';
import { theme, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import SpinCentered from '@/comp/SpinCentered';
import { EPSelect } from '@/components';
import { EngineDeactivationReasonEnum, EngineDeactivationReasonLabels } from '@/constants/engine-deactivation-reason';
import { PopconfirmTitleContainer } from './styled';
import { DefaultSetpointsOption } from '@/constants/enums';

interface PopconfirmTitleProps {
  isProactiveEngineStateActivated: boolean;
  isLoading: boolean;
  setCommentRef: (value: EngineDeactivationReasonEnum) => void;
  setComment: (value: string) => void;
  setIsError: (value: boolean) => void;
  defaultSetpointsOption?: DefaultSetpointsOption;
  isError: boolean;
}

const { TextArea } = Input;

const { useToken } = theme;

const PopconfirmTitle = forwardRef(
  (
    {
      isProactiveEngineStateActivated,
      isLoading,
      setCommentRef,
      setComment,
      setIsError,
      defaultSetpointsOption,
      isError,
    }: PopconfirmTitleProps,
    ref: React.Ref<{ resetControls: () => void }>,
  ) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const [showFreeText, setShowFreeText] = useState(false);
    const textAreaRef = useRef(null);
    const initialFormState = {
      selectedReasonRef: null,
    };
    const [formState, setFormState] = useState(initialFormState);
    const reasons = Object.values(EngineDeactivationReasonEnum)
      .filter(key => typeof key === 'number')
      .map(key => ({
        value: key,
        label: EngineDeactivationReasonLabels[key],
      }));

    React.useImperativeHandle(ref, () => ({
      resetControls,
    }));
    const handleSelectChange = (key: EngineDeactivationReasonEnum) => {
      setFormState(prevState => ({
        ...prevState,
        selectedReasonRef: key,
      }));
      setIsError(false);
      setCommentRef(key);
      checkFreeTextReason(key);
    };

    const checkFreeTextReason = (value: EngineDeactivationReasonEnum) => {
      const isFreeTextReason =
        value === EngineDeactivationReasonEnum.OtherReason || value === EngineDeactivationReasonEnum.EcoplantReason;

      if (!isFreeTextReason) {
        setComment(null);
        if (textAreaRef.current) {
          textAreaRef.current.value = '';
        }
      }
      setShowFreeText(isFreeTextReason);
    };

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (textAreaRef.current) {
        textAreaRef.current.value = e.target.value;
        setComment(e.target.value);
      }
    };
    const resetControls = () => {
      setIsError(false);
      setCommentRef(null);
      setComment(null);
      setShowFreeText(false);
      if (textAreaRef.current) {
        textAreaRef.current.value = '';
      }
      setFormState({
        selectedReasonRef: null,
      });
    };

    const deactivationContentMap = {
      [DefaultSetpointsOption.Baseline]: t('stationControlDefaultSetpointsBaseline'),
      [DefaultSetpointsOption.Custom]: t('stationControlDefaultSetpointsCustom'),
    };

    const deactivationContent = deactivationContentMap[defaultSetpointsOption] || t('deactivationContent');

    return (
      <PopconfirmTitleContainer>
        <span className="text">{deactivationContent}</span>
        <div className="reason-container">
          {isLoading ? (
            <SpinCentered color={token.colorPrimary} className="spinner" />
          ) : isProactiveEngineStateActivated ? (
            <>
              <p>{t('Reason for Deactivation')}:</p>
              <div className="ep-select">
                <EPSelect
                  value={formState.selectedReasonRef}
                  onChange={handleSelectChange}
                  allowClear
                  placeholder={t('SelectReason')}
                  options={reasons}
                />
              </div>
              {showFreeText && (
                <div className="comments-container">
                  <p>{t('Comment')}:</p>
                  <TextArea
                    onChange={handleTextAreaChange}
                    placeholder={t('provideReason')}
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    ref={textAreaRef}
                    className={isError ? 'error' : ''}
                    maxLength={200}
                  />
                </div>
              )}
              {isError && <span className="error-message">{t('provideReason')}</span>}
            </>
          ) : null}
        </div>
      </PopconfirmTitleContainer>
    );
  },
);

export default PopconfirmTitle;
